import React, { useState, useRef, useEffect } from "react"

import useProductTracking from "hooks/useProductTracking"
import { applyVideoCloudinaryTransforms } from "utils/applyCloudinaryTransforms"
import { formatCurrency } from "utils/currency"

import {
  Wrapper,
  ScrollContainer,
  ScrollableGrid,
  Container,
  VideoImageWrapper,
  Video,
  VideoPlayIcon,
  ImageRegular,
  ImageCropped,
  VideoProgress,
  VideoProgressBar,
  Content,
  SmallImage,
  Meta,
  TitleWrapper,
  Title,
  Description,
  BuyButton,
  CloseIcon,
} from "./styles"

const VideoCarouselModalMobile = ({ items, idx, onCloseModal }) => {
  const refScrollable = useRef(null)
  useEffect(() => {
    const container = refScrollable.current
    const marginRight = parseFloat(getComputedStyle(container.firstChild).marginRight)
    const itemOffests = Array.from(container.children).map((child) => child.offsetLeft)
    container.scrollLeft = itemOffests[idx] - marginRight
  }, [idx])

  return (
    <Wrapper>
      <ScrollContainer>
        <ScrollableGrid ref={refScrollable}>
          {items.map((item, i) => (
            <Item key={item.id} {...item} autoPlay={idx === i} />
          ))}
        </ScrollableGrid>
      </ScrollContainer>
      <CloseIcon id="close-icon" width={14} height={14} onClick={onCloseModal} />
    </Wrapper>
  )
}

const Item = ({
  image,
  imageCrops,
  shopifyProductVariant: { price, product },
  previewVideo: video,
  autoPlay,
}) => {
  const { description, image: productImage, media, onlineStoreUrl, productId, title } = product

  const { trackProductClick } = useProductTracking()
  const refBuyButton = useRef()
  trackProductClick({
    elementRef: refBuyButton,
    item: {
      itemType: "Product",
      itemId: productId,
      shopifyProductVariant: { price, product: { title } },
    },
  })

  const refImage = useRef(null)
  const refVideo = useRef(null)
  const refProgressBar = useRef(null)
  const [isPlaying, setPlaying] = useState(autoPlay)

  const onPlayIconClick = () => {
    if (refVideo.current.paused) {
      refVideo.current.play()
    } else {
      refVideo.current.pause()
    }
  }

  const onVideoClick = () => {
    if (!refVideo.current.paused) {
      refVideo.current.pause()
    }
  }

  const handlePlay = () => {
    setPlaying(true)
  }

  const handlePause = () => {
    setPlaying(false)
  }

  useEffect(() => {
    if (isPlaying && refVideo.current && refImage.current) {
      refVideo.current.style.opacity = 1
      refImage.current.style.opacity = 0
    }
  }, [isPlaying])

  const handleProgress = () => {
    if (refVideo.current && refProgressBar.current) {
      const progressPercentage = (refVideo.current.currentTime / refVideo.current.duration) * 100
      refProgressBar.current.style.width = `${progressPercentage}%`
    }
  }

  useEffect(() => {
    if (refVideo.current && refProgressBar.current) {
      refVideo.current.addEventListener("play", handlePlay)
      refVideo.current.addEventListener("pause", handlePause)
      refVideo.current.addEventListener("timeupdate", handleProgress)
    }

    return () => {
      if (refVideo.current && refProgressBar.current) {
        refVideo.current.removeEventListener("play", handlePlay)
        refVideo.current.removeEventListener("pause", handlePause)
        refVideo.current.removeEventListener("timeupdate", handleProgress)
      }
    }
  }, [video])

  const imageUrl = image && image.length && image[0].secure_url
  const imageElement = imageUrl ? (
    <ImageRegular
      elRef={refImage}
      variant="4col"
      baseUrl={imageUrl}
      images={[{ transforms: "c_fill,g_auto,w_350,h_518,q_auto,f_auto,fl_lossy" }]}
      isGrid
      imagePosition="top"
    />
  ) : imageCrops ? (
    <ImageCropped
      elRef={refImage}
      variant="4col"
      imageData={imageCrops}
      sizes={[
        {
          query: 0,
          width: mediaWidth,
          height: mediaHeight,
          transforms: "c_fill,g_auto,w_350,h_518,q_auto,f_auto,fl_lossy",
        },
      ]}
      isGrid
      imagePosition="top"
    />
  ) : null

  return (
    <Container>
      <VideoImageWrapper>
        {video ? (
          <>
            {imageElement}
            <Video
              ref={refVideo}
              width="100%"
              autoPlay={autoPlay}
              muted={false}
              loop
              playsInline
              type="video/mp4"
              src={applyVideoCloudinaryTransforms(
                video.videoObject[0].secure_url,
                `c_fill,w_350,h_518`,
              )}
              onClick={onVideoClick}
            />
            {!isPlaying && (
              <VideoPlayIcon id="video-play" width={58} height={58} onClick={onPlayIconClick} />
            )}
            <VideoProgress>
              <VideoProgressBar ref={refProgressBar} />
            </VideoProgress>
          </>
        ) : (
          <ImageRegular
            baseUrl={productImage || media[0]?.image.originalSrc}
            images={[{ transforms: "c_fill,g_auto,w_350,h_518,q_auto,f_auto,fl_lossy" }]}
          />
        )}
      </VideoImageWrapper>
      <Content>
        <SmallImage
          baseUrl={productImage || media[0]?.image.originalSrc}
          images={[{ transforms: "c_fill,g_auto,w_100,h_100,q_auto,f_auto,fl_lossy" }]}
        />
        <Meta>
          <TitleWrapper>
            <Title>{title}</Title>
            <Title>{formatCurrency(price, true)}</Title>
          </TitleWrapper>
          {description && <Description>{description}</Description>}
          <a
            ref={refBuyButton}
            href={onlineStoreUrl}
            title={title}
            target="_blank"
            rel="noreferrer"
          >
            <BuyButton>BUY NOW</BuyButton>
          </a>
        </Meta>
      </Content>
    </Container>
  )
}

export default VideoCarouselModalMobile
